import { useState, useCallback, useMemo } from 'react';

import type { OcrData } from '../types';
import {
  buildFieldToExpenseMap,
  buildHighlightId,
  buildHighlightIdToFieldMap,
} from '../utils/ocr-field-mapping-utils';

/**
 * Manages bidirectional highlight state between booking fields and document preview.
 * - Field → Document: onFieldFocus sets active highlights on the preview
 * - Document → Field: onHighlightClick sets activeFieldId on the form
 *
 * Caller is responsible for enriching ocrData (e.g., enrichLineItemMappings)
 * before passing it in — this hook treats ocrData as ground truth.
 */
export const useHighlightInteraction = (
  ocrData: OcrData | null | undefined
) => {
  const [activeFieldId, setActiveFieldId] = useState<string | null>(null);

  // Build maps once from ocrData
  const fieldToExpenseMap = useMemo(
    () => buildFieldToExpenseMap(ocrData),
    [ocrData]
  );
  const highlightIdToFieldMap = useMemo(
    () => buildHighlightIdToFieldMap(ocrData),
    [ocrData]
  );

  // Compute activeHighlightIds: which highlight IDs in InvoicePreviewPanel should be active
  const activeHighlightIds = useMemo(() => {
    if (!activeFieldId || !ocrData?.expenseFields) return new Set<string>();

    const ids = new Set<string>();
    ocrData.expenseFields.forEach((field, index) => {
      if (
        field.bookingFieldMapping === activeFieldId &&
        field.valueBoundingBox
      ) {
        ids.add(buildHighlightId(field.fieldType, index));
      }
    });
    return ids;
  }, [activeFieldId, ocrData]);

  // Field → Document: user focuses a booking field
  const onFieldFocus = useCallback(
    (fieldId: string) => {
      // Activate if matching expense field exists, otherwise clear highlights
      setActiveFieldId(fieldToExpenseMap.has(fieldId) ? fieldId : null);
    },
    [fieldToExpenseMap]
  );

  // Document → Field: user clicks a highlight in the preview
  const onHighlightClick = useCallback(
    (highlightId: string) => {
      const fieldId = highlightIdToFieldMap.get(highlightId);
      if (fieldId) {
        setActiveFieldId(fieldId);
      }
    },
    [highlightIdToFieldMap]
  );

  const clearHighlights = useCallback(() => {
    setActiveFieldId(null);
  }, []);

  return {
    activeFieldId,
    activeHighlightIds,
    onFieldFocus,
    onHighlightClick,
    clearHighlights,
  };
};
